/**
 * @module features/auth/components/LoginForm
 * @description Minimalist Super Admin login form adhering to DESIGN.md and lead-react-engineer standards.
 * Features an adaptive multi-device interface:
 * - Mobile (<640px): Sheet overlay with grab handle, safe-area padding, and thumb-friendly touch targets.
 * - Desktop (>=640px): Floating card with integrated brand header.
 * Consumes branding dynamically from environment configuration.
 */

import * as React from "react";
import { useForm } from "react-hook-form";
import {
  Fingerprint,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowUp,
  X,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { TwoFactorDialog } from "./TwoFactorDialog";
import {
  loginWithPassword,
  getPasskeyLoginOptions,
  verifyPasskeyLogin,
  loginWithGoogle,
} from "../api/auth-api";
import {
  authenticateWithPasskey,
  browserSupportsWebAuthn,
} from "@/lib/webauthn";
import { AppError } from "@/lib/api-client";
import { UserDto } from "@/types/auth";

interface LoginFormProps {
  onSuccess: (user: UserDto) => void;
}

interface LoginFormValues {
  email: string;
  password: string;
}

const REMEMBERED_EMAIL_KEY = "rxdg_admin_email_pref";

export function LoginForm({ onSuccess }: LoginFormProps) {
  // Client-safe email restoration (OWASP compliant: only email string, never passwords/tokens)
  const initialEmail = React.useMemo(() => {
    try {
      return localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
    } catch {
      return "";
    }
  }, []);

  const [rememberEmail, setRememberEmail] = React.useState<boolean>(
    Boolean(initialEmail)
  );

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: initialEmail, password: "" },
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = React.useState(false);

  // 2FA challenge state
  const [temp2faToken, setTemp2faToken] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (initialEmail) {
      setValue("email", initialEmail);
    }
  }, [initialEmail, setValue]);

  const handlePasswordKeyEvent = (e: React.KeyboardEvent) => {
    setIsCapsLockOn(e.getModifierState("CapsLock"));
  };

  const persistEmailPreference = (email: string) => {
    try {
      if (rememberEmail && email.trim()) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
    } catch {
      // Ignore storage errors in restricted contexts
    }
  };

  const onSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await loginWithPassword(values);
      persistEmailPreference(values.email);

      if (result.requires_2fa && result.temp_token) {
        setTemp2faToken(result.temp_token);
      } else if (result.user) {
        onSuccess(result.user);
      }
    } catch (err: unknown) {
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
      } else {
        setErrorMessage("Sign in failed. Please check your credentials.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true);
    setErrorMessage(null);

    try {
      const email = getValues("email");
      const options = await getPasskeyLoginOptions(email ? email.trim() : undefined);
      const credential = await authenticateWithPasskey(options);
      const result = await verifyPasskeyLogin(credential);

      if (email) persistEmailPreference(email);
      onSuccess(result.user);
    } catch (err: any) {
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
      } else if (err.name === "NotAllowedError") {
        // User cancelled or biometric prompt timed out
      } else {
        setErrorMessage(err.message || "Passkey authentication was not completed.");
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleGoogleClick = () => {
    const token = window.prompt("Enter Google OAuth ID token for SSO verification:");
    if (token) {
      setIsLoading(true);
      setErrorMessage(null);
      loginWithGoogle(token)
        .then((res) => {
          if (res.user?.email_id) persistEmailPreference(res.user.email_id);
          onSuccess(res.user);
        })
        .catch((err) => {
          if (err instanceof AppError) setErrorMessage(err.errorDetails);
          else setErrorMessage("Google SSO authentication failed.");
        })
        .finally(() => setIsLoading(false));
    }
  };

  const hasWebAuthn = browserSupportsWebAuthn();

  return (
    <div className="w-full flex flex-col">
      {/* Header Typography */}
      <div className="text-center mb-6">
        <h1 className="text-2xl sm:text-[28px] font-bold tracking-tight text-foreground font-sans">
          Welcome back
        </h1>
        <p className="text-xs sm:text-[13px] text-muted-foreground mt-2 leading-relaxed">
          Enter your credentials to continue
        </p>
      </div>

      <div className="space-y-4">
        {/* Dismissible Error Alert */}
        {errorMessage && (
          <div
            role="alert"
            className="flex items-start justify-between gap-2.5 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-400 animate-in fade-in duration-200"
          >
            <div className="flex items-start gap-2 min-w-0">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="leading-snug break-words">{errorMessage}</span>
            </div>
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              className="text-red-600/70 hover:text-red-700 p-0.5 rounded transition-colors shrink-0"
              aria-label="Dismiss error"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* 2-Column Split: Form on Left, "or" in Center, Google & Passkey on Right */}
        <div className="flex flex-col sm:flex-row items-stretch gap-6 sm:gap-8">
          {/* Left Column: Credentials Form */}
          <div className="flex-1 min-w-0">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5" noValidate>
              {/* Email Field */}
              <div className="space-y-1.5 text-left">
                <label
                  htmlFor="login-email"
                  className="text-xs font-semibold text-foreground block"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  placeholder="Type your email"
                  className="h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-white transition-all shadow-xs"
                  {...register("email", {
                    required: "Email address is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Please enter a valid email address",
                    },
                  })}
                />
                {errors.email && (
                  <span className="text-[11px] text-danger block">
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1.5 text-left">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="login-password"
                    className="text-xs font-semibold text-foreground"
                  >
                    Password
                  </label>
                  {isCapsLockOn && (
                    <span
                      className="text-[11px] text-warning flex items-center gap-1 font-medium"
                      role="status"
                    >
                      <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      Caps Lock on
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Type your password"
                    className="h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 pr-10 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-white transition-all shadow-xs"
                    onKeyDown={handlePasswordKeyEvent}
                    onKeyUp={handlePasswordKeyEvent}
                    {...register("password", { required: "Password is required" })}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-muted-foreground/70 hover:text-foreground rounded transition-colors focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="text-[11px] text-danger block">
                    {errors.password.message}
                  </span>
                )}
              </div>

              {/* Remember Email Option */}
              <div className="flex items-center gap-2 pt-0.5">
                <Checkbox
                  id="remember-email"
                  checked={rememberEmail}
                  onCheckedChange={setRememberEmail}
                />
                <label
                  htmlFor="remember-email"
                  className="text-xs text-muted-foreground hover:text-foreground cursor-pointer select-none transition-colors"
                >
                  Remember email
                </label>
              </div>

              {/* Primary Submit Button */}
              <button
                type="submit"
                disabled={isLoading || isPasskeyLoading}
                className="w-full h-10 rounded-md bg-zinc-950 dark:bg-zinc-100 hover:bg-zinc-900 dark:hover:bg-white text-white dark:text-zinc-950 text-xs sm:text-sm font-medium flex items-center justify-center transition-colors shadow-xs mt-2"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Continuing...</span>
                  </span>
                ) : (
                  "Continue with email"
                )}
              </button>
            </form>
          </div>

          {/* Center Divider: Vertical on sm+, horizontal on mobile */}
          <div className="relative flex items-center justify-center py-2 sm:py-0 sm:px-2">
            {/* Desktop/Tablet vertical line */}
            <div className="hidden sm:block absolute inset-y-2 left-1/2 -translate-x-1/2 w-[1px] bg-zinc-200 dark:bg-zinc-800" />
            {/* Mobile horizontal line */}
            <div className="sm:hidden absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-zinc-200 dark:bg-zinc-800" />
            {/* 'or' badge */}
            <span className="relative z-10 bg-background dark:bg-zinc-950 px-3 py-1 text-xs text-muted-foreground select-none uppercase tracking-wider font-mono">
              or
            </span>
          </div>

          {/* Right Column: Google Sign-in & Passkey Fast Auth */}
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-3">
            {/* Continue with Google Button */}
            <button
              type="button"
              onClick={handleGoogleClick}
              disabled={isLoading || isPasskeyLoading}
              className="w-full h-11 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800/80 text-foreground text-xs sm:text-sm font-medium flex items-center justify-center gap-2.5 transition-colors shadow-xs"
            >
              <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
              <span>Continue with Google</span>
            </button>

            {/* Passkey Fast Auth (if browser supports WebAuthn) */}
            {hasWebAuthn && (
              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={isPasskeyLoading || isLoading}
                className="w-full h-11 rounded-md bg-zinc-950 dark:bg-zinc-100 hover:bg-zinc-900 dark:hover:bg-white text-white dark:text-zinc-950 text-xs sm:text-sm font-medium flex items-center justify-center gap-2.5 transition-colors shadow-xs"
              >
                {isPasskeyLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-white dark:text-zinc-950" />
                ) : (
                  <Fingerprint className="h-4 w-4 text-white dark:text-zinc-950 shrink-0" />
                )}
                <span>Sign in with Passkey</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2FA Challenge Modal */}
      {temp2faToken && (
        <TwoFactorDialog
          open={Boolean(temp2faToken)}
          tempToken={temp2faToken}
          onSuccess={(res) => {
            setTemp2faToken(null);
            onSuccess(res.user);
          }}
          onCancel={() => setTemp2faToken(null)}
        />
      )}
    </div>
  );
}
